import { useEffect, useState } from 'react'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Clock, AlertTriangle, LogOut } from 'lucide-react'
import { useAuthStore } from '@/app/store/auth'

interface SessionTimeoutDialogProps {
    isOpen: boolean
    onExtendSession: () => void
    onLogout: () => void
    timeRemaining: number
}

export function SessionTimeoutDialog({
    isOpen,
    onExtendSession,
    onLogout,
    timeRemaining
}: SessionTimeoutDialogProps) {
    const [countdown, setCountdown] = useState(timeRemaining)
    const { user } = useAuthStore()

    // Update countdown every second
    useEffect(() => {
        if (!isOpen) return

        setCountdown(timeRemaining)

        const interval = setInterval(() => {
            setCountdown(prev => {
                if (prev <= 1) {
                    clearInterval(interval)
                    onLogout() // Auto-logout when countdown reaches 0
                    return 0
                }
                return prev - 1
            })
        }, 1000)

        return () => clearInterval(interval)
    }, [isOpen, timeRemaining, onLogout])

    const formatTime = (seconds: number) => {
        const mins = Math.floor(seconds / 60)
        const secs = seconds % 60
        return `${mins}:${secs.toString().padStart(2, '0')}`
    }

    const handleExtendSession = () => {
        onExtendSession()
    }

    const handleLogout = () => {
        onLogout()
    }

    return (
        <Dialog open={isOpen} onOpenChange={() => { }}>
            <DialogContent className="sm:max-w-md" onPointerDownOutside={(e) => e.preventDefault()}>
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Clock className="h-5 w-5 text-orange-500" />
                        Session Timeout Warning
                    </DialogTitle>
                    <DialogDescription>
                        Your session will expire soon. Please choose an action to continue.
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                    <Alert>
                        <AlertTriangle className="h-4 w-4" />
                        <AlertDescription>
                            <strong>Time remaining:</strong> {formatTime(countdown)}
                            <br />
                            <span className="text-sm text-muted-foreground">
                                Your session will automatically end in {countdown} seconds.
                            </span>
                        </AlertDescription>
                    </Alert>

                    <div className="text-sm text-muted-foreground">
                        <p>Hello {user?.name || 'User'},</p>
                        <p>
                            Your session has been inactive and will expire soon for security reasons.
                            You can extend your session to continue working, or log out safely.
                        </p>
                    </div>
                </div>

                <DialogFooter className="flex-col sm:flex-row gap-2">
                    <Button
                        variant="outline"
                        onClick={handleLogout}
                        className="flex items-center gap-2"
                    >
                        <LogOut className="h-4 w-4" />
                        Log Out
                    </Button>
                    <Button
                        onClick={handleExtendSession}
                        className="flex items-center gap-2"
                    >
                        <Clock className="h-4 w-4" />
                        Extend Session
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    )
}
